import React from "react";
import { Button, Modal } from "react-bootstrap";
import { FaCheck, FaTimes } from "react-icons/fa";
import "./ProjectInfoModal.css";
import ProjectFormsPanel from "./ProjectFormsPanel";
import LinkifiedText from "./LinkifiedText";
import ProjectCommunicationLinks from "./ProjectCommunicationLinks";

const formatDateTime = (value) => {
  if (!value) return "Ongoing";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not specified";

  return new Intl.DateTimeFormat("en-ZA", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const displayValue = (value, fallback = "Not specified") => {
  if (value === null || value === undefined || value === "") return fallback;
  return value;
};

const formatHours = (hours) => {
  const minimum = hours?.minimum;
  const maximum = hours?.maximum;
  if (minimum === undefined && maximum === undefined) return "Not specified";
  if (minimum !== undefined && maximum !== undefined) return `${minimum}–${maximum} hours per week`;
  if (minimum !== undefined) return `At least ${minimum} hours per week`;
  return `Up to ${maximum} hours per week`;
};

const formatAgeRange = (type) => {
  const minimum = type.minimumAge;
  const maximum = type.maximumAge;
  if (minimum === undefined && maximum === undefined) return "No age restriction specified";
  if (minimum !== undefined && maximum !== undefined) return `${minimum}–${maximum} years`;
  if (minimum !== undefined) return `${minimum} years and older`;
  return `Up to ${maximum} years`;
};

const DefinitionList = ({ items }) => (
  <dl className="project-info-facts">
    {items.map(({ label, value }) => (
      <div key={label} className="project-info-fact">
        <dt>{label}</dt>
        <dd>{displayValue(value)}</dd>
      </div>
    ))}
  </dl>
);

const TagList = ({ items, emptyLabel }) => {
  if (!items?.length) return <span className="project-info-empty-inline">{emptyLabel}</span>;

  return (
    <ul className="project-info-tags">
      {items.map((item) => <li key={item}>{item}</li>)}
    </ul>
  );
};

const ProjectInfoModal = ({ show, onHide, project }) => {
  if (!project) return null;

  const isOnline = project.locationType === "Online";
  const transportProvided = Boolean(project.location?.transportProvided);
  const participantTypes = project.participantTypes || [];

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      centered
      scrollable
      dialogClassName="project-info-dialog"
      aria-labelledby="project-info-title"
    >
      <Modal.Header closeButton className="project-info-header">
        <div className="project-info-heading">
          <Modal.Title id="project-info-title">{project.name}</Modal.Title>
          <p>
            {displayValue(project.organiserName, "Unknown organiser")} · {displayValue(project.organiserType)}
          </p>
        </div>
      </Modal.Header>

      <Modal.Body className="project-info-body">
        <div className="project-info-summary" aria-label="Project summary">
          <span>{displayValue(project.locationType, "Format not specified")}</span>
          <span>{displayValue(project.visibility, "Visibility not specified")}</span>
          <span>{project.endDate ? `Ends ${formatDateTime(project.endDate)}` : "Ongoing project"}</span>
        </div>

        <section className="project-info-section">
          <h3>About this project</h3>
          <p className="project-info-description">
            <LinkifiedText text={displayValue(project.description, "No project description has been provided.")} />
          </p>
        </section>

        <ProjectCommunicationLinks project={project} />

        <ProjectFormsPanel projectId={project._id} visible={show} />

        <div className="project-info-two-column">
          <section className="project-info-section">
            <h3>Organisation</h3>
            <DefinitionList items={[
              { label: "Organiser", value: project.organiserName },
              { label: "Organiser type", value: project.organiserType },
              { label: "Visibility", value: project.visibility },
            ]} />
          </section>

          <section className="project-info-section">
            <h3>Schedule</h3>
            <DefinitionList items={[
              { label: "Starts", value: formatDateTime(project.startDate) },
              { label: "Ends", value: project.endDate ? formatDateTime(project.endDate) : "Ongoing" },
              { label: "Time commitment", value: formatHours(project.weeklyHoursRequired) },
            ]} />
          </section>
        </div>

        <section className="project-info-section">
          <h3>Focus areas</h3>
          <div className="project-info-tag-groups">
            <div>
              <h4>Categories</h4>
              <TagList items={project.categories} emptyLabel="No categories specified" />
            </div>
            <div>
              <h4>Sustainable Development Goals</h4>
              <TagList items={project.sdgs} emptyLabel="No SDGs specified" />
            </div>
          </div>
        </section>

        <div className="project-info-two-column">
          <section className="project-info-section">
            <h3>Location</h3>
            {isOnline ? (
              <p className="project-info-note">This project is delivered online.</p>
            ) : (
              <DefinitionList items={[
                { label: "Format", value: project.locationType },
                { label: "Address", value: project.location?.address },
              ]} />
            )}
          </section>

          {!isOnline && (
            <section className="project-info-section">
              <h3>Transport</h3>
              <div className="project-info-transport-status">
                {transportProvided ? <FaCheck aria-hidden="true" /> : <FaTimes aria-hidden="true" />}
                <span>{transportProvided ? "Transport provided" : "Transport not provided"}</span>
              </div>

              {transportProvided && (
                <>
                  <DefinitionList items={[
                    { label: "Transport type", value: project.location?.transportType },
                    { label: "Details", value: project.location?.transportDetails },
                  ]} />
                  {project.location?.transportRules?.length > 0 && (
                    <div className="project-info-rules">
                      <h4>Transport rules</h4>
                      <ul>
                        {project.location.transportRules.map((rule) => <li key={rule}>{rule}</li>)}
                      </ul>
                    </div>
                  )}
                </>
              )}
            </section>
          )}
        </div>

        <section className="project-info-section">
          <h3>Participant requirements</h3>
          {participantTypes.length ? (
            <div className="project-info-requirements">
              {participantTypes.map((type, index) => (
                <article key={`${type.role || "participant"}-${index}`}>
                  <h4>{displayValue(type.role, `Participant type ${index + 1}`)}</h4>
                  <DefinitionList items={[
                    { label: "Experience", value: type.experienceLevel },
                    { label: "Age range", value: formatAgeRange(type) },
                  ]} />
                  <div className="project-info-requirement-group">
                    <h5>Required skills</h5>
                    <TagList items={type.skillsRequired} emptyLabel="No specific skills required" />
                  </div>
                  {type.additionalRequirements && (
                    <div className="project-info-requirement-group">
                      <h5>Additional requirements</h5>
                      <p><LinkifiedText text={type.additionalRequirements} /></p>
                    </div>
                  )}
                </article>
              ))}
            </div>
          ) : (
            <p className="project-info-note">No participant requirements have been specified.</p>
          )}
        </section>
      </Modal.Body>

      <Modal.Footer className="project-info-footer">
        <Button variant="outline-secondary" onClick={onHide}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ProjectInfoModal;
